import type {
  CardId,
  CollectionId,
  DatabaseId,
  ErdParams,
  FieldId,
  MeasureId,
  NativeQuerySnippetId,
  SchemaName,
  SegmentId,
  TableId,
  WritebackActionId,
} from "metabase-types/api";

const ROOT_URL = "/data-studio";

type OptionalParams = {
  collectionId?: CollectionId;
};

function getQueryString({ collectionId }: OptionalParams) {
  const searchParams = new URLSearchParams();
  if (collectionId != null) {
    searchParams.set("collectionId", String(collectionId));
  }
  const queryString = searchParams.toString();
  return queryString.length > 0 ? `?${queryString}` : "";
}

export const DATA_STUDIO_TABLE_METADATA_TABS = [
  "details",
  "field",
  "segments",
  "measures",
] as const;
export type DataStudioTableMetadataTab =
  (typeof DATA_STUDIO_TABLE_METADATA_TABS)[number];

export function isDataStudioTableMetadataTab(
  tab: unknown,
): tab is DataStudioTableMetadataTab {
  return DATA_STUDIO_TABLE_METADATA_TABS.includes(
    // Unjustified type cast. FIXME
    tab as DataStudioTableMetadataTab,
  );
}

type DataStudioDataParams = {
  databaseId?: DatabaseId;
  schemaName?: SchemaName | null;
  tableId?: TableId;
  tab?: DataStudioTableMetadataTab;
  fieldId?: FieldId;
};

export function dataStudio() {
  return ROOT_URL;
}

export function dataStudioGuide() {
  return `${ROOT_URL}/guide`;
}

export function dataStudioData({
  databaseId,
  schemaName,
  tableId,
  tab,
  fieldId,
}: DataStudioDataParams = {}) {
  const parts = [ROOT_URL, "data"];

  if (databaseId != null) {
    parts.push("database", String(databaseId));

    if (schemaName != null) {
      const schemaId = `${databaseId}:${encodeURIComponent(schemaName)}`;
      parts.push("schema", schemaId);

      if (tableId != null) {
        parts.push("table", String(tableId));

        if (tab != null) {
          parts.push(tab);

          if (fieldId != null && tab === "field") {
            parts.push(String(fieldId));
          }
        }
      }
    }
  }

  return parts.join("/");
}

type ExpandedIdsParams = { expandedIds?: CollectionId[] };

function getExpandedIdsQueryString({ expandedIds }: ExpandedIdsParams) {
  if (!expandedIds?.length) {
    return "";
  }
  const params = new URLSearchParams();
  expandedIds.forEach((id) => params.append("expandedId", String(id)));
  return `?${params.toString()}`;
}

export function dataStudioLibrary(params: ExpandedIdsParams = {}) {
  return `${ROOT_URL}/library${getExpandedIdsQueryString(params)}`;
}

export function dataStudioSnippets(params: ExpandedIdsParams = {}) {
  return `${ROOT_URL}/snippets${getExpandedIdsQueryString(params)}`;
}

export function dataStudioActions(params: ExpandedIdsParams = {}) {
  return `${ROOT_URL}/actions${getExpandedIdsQueryString(params)}`;
}

export function dataStudioTable(tableId: TableId) {
  return `${dataStudioLibrary()}/tables/${tableId}`;
}

export function dataStudioTableFields(tableId: TableId, fieldId?: FieldId) {
  const baseUrl = `${dataStudioLibrary()}/tables/${tableId}/fields`;
  return fieldId != null ? `${baseUrl}/${fieldId}` : baseUrl;
}

export function dataStudioTableDependencies(tableId: TableId) {
  return `${dataStudioTable(tableId)}/dependencies`;
}

export function dataStudioTableSegments(tableId: TableId) {
  return `${dataStudioTable(tableId)}/segments`;
}

export function dataStudioPublishedTableSegmentNew(tableId: TableId) {
  return `${dataStudioTableSegments(tableId)}/new`;
}

export function dataStudioPublishedTableSegment(
  tableId: TableId,
  segmentId: SegmentId,
) {
  return `${dataStudioTableSegments(tableId)}/${segmentId}`;
}

export function dataStudioPublishedTableSegmentRevisions(
  tableId: TableId,
  segmentId: SegmentId,
) {
  return `${dataStudioPublishedTableSegment(tableId, segmentId)}/revisions`;
}

export function dataStudioPublishedTableSegmentDependencies(
  tableId: TableId,
  segmentId: SegmentId,
) {
  return `${dataStudioPublishedTableSegment(tableId, segmentId)}/dependencies`;
}

type DataModelSegmentParams = {
  databaseId: DatabaseId;
  schemaName: SchemaName;
  tableId: TableId;
  segmentId: SegmentId;
};

export function dataStudioDataModelSegment({
  databaseId,
  schemaName,
  tableId,
  segmentId,
}: DataModelSegmentParams) {
  return `${dataStudioData({ databaseId, schemaName, tableId, tab: "segments" })}/${segmentId}`;
}

export function dataStudioDataModelSegmentRevisions(
  params: DataModelSegmentParams,
) {
  return `${dataStudioDataModelSegment(params)}/revisions`;
}

export function dataStudioDataModelSegmentDependencies(
  params: DataModelSegmentParams,
) {
  return `${dataStudioDataModelSegment(params)}/dependencies`;
}

export function newDataStudioDataModelSegment({
  databaseId,
  schemaName,
  tableId,
}: Omit<DataModelSegmentParams, "segmentId">) {
  return `${dataStudioData({ databaseId, schemaName, tableId, tab: "segments" })}/new`;
}

export function dataStudioTableMeasures(tableId: TableId) {
  return `${dataStudioTable(tableId)}/measures`;
}

export function dataStudioPublishedTableMeasureNew(tableId: TableId) {
  return `${dataStudioTableMeasures(tableId)}/new`;
}

export function dataStudioPublishedTableMeasure(
  tableId: TableId,
  measureId: MeasureId,
) {
  return `${dataStudioTableMeasures(tableId)}/${measureId}`;
}

export function dataStudioPublishedTableMeasureDependencies(
  tableId: TableId,
  measureId: MeasureId,
) {
  return `${dataStudioPublishedTableMeasure(tableId, measureId)}/dependencies`;
}

type DataModelMeasureParams = {
  databaseId: DatabaseId;
  schemaName: SchemaName;
  tableId: TableId;
  measureId: MeasureId;
};

export function dataStudioDataModelMeasure({
  databaseId,
  schemaName,
  tableId,
  measureId,
}: DataModelMeasureParams) {
  return `${dataStudioData({ databaseId, schemaName, tableId, tab: "measures" })}/${measureId}`;
}

export function dataStudioDataModelMeasureDependencies(
  params: DataModelMeasureParams,
) {
  return `${dataStudioDataModelMeasure(params)}/dependencies`;
}

export function dataStudioDataModelMeasureRevisions(
  params: DataModelMeasureParams,
) {
  return `${dataStudioDataModelMeasure(params)}/revisions`;
}

export function dataStudioPublishedTableMeasureRevisions(
  tableId: TableId,
  measureId: MeasureId,
) {
  return `${dataStudioPublishedTableMeasure(tableId, measureId)}/revisions`;
}

export function newDataStudioDataModelMeasure({
  databaseId,
  schemaName,
  tableId,
}: Omit<DataModelMeasureParams, "measureId">) {
  return `${dataStudioData({ databaseId, schemaName, tableId, tab: "measures" })}/new`;
}

export type NewDataStudioMetricProps = {
  collectionId?: CollectionId;
};

export function newDataStudioMetric(params: NewDataStudioMetricProps = {}) {
  return `${dataStudioLibrary()}/metrics/new${getQueryString(params)}`;
}

export function dataStudioMetric(cardId: CardId) {
  return `${dataStudioLibrary()}/metrics/${cardId}`;
}

export function dataStudioMetricOverview(cardId: CardId) {
  return `${dataStudioMetric(cardId)}/overview`;
}

export function dataStudioMetricQuery(cardId: CardId) {
  return `${dataStudioMetric(cardId)}/query`;
}

export function dataStudioMetricDimensions(cardId: CardId) {
  return `${dataStudioMetric(cardId)}/dimensions`;
}

export function dataStudioMetricDependencies(cardId: CardId) {
  return `${dataStudioMetric(cardId)}/dependencies`;
}

export function dataStudioMetricHistory(cardId: CardId) {
  return `${dataStudioMetric(cardId)}/history`;
}

type DataStudioSchemaViewerParams = {
  databaseId: DatabaseId;
  schema?: SchemaName;
  tableIds?: readonly TableId[];
};

export function getSchemaViewerParams({
  databaseId,
  schema,
  tableIds,
}: DataStudioSchemaViewerParams): ErdParams {
  const params: ErdParams = { "database-id": databaseId };
  if (schema != null) {
    params.schema = schema;
  }
  if (tableIds != null && tableIds.length > 0) {
    params["table-ids"] = [...tableIds];
  }
  return params;
}

export function dataStudioSchemaViewer(args?: DataStudioSchemaViewerParams) {
  const SCHEMA_VIEWER_BASE_URL = `${ROOT_URL}/schema-viewer`;
  if (!args) {
    return SCHEMA_VIEWER_BASE_URL;
  }

  const queryParams = getSchemaViewerParams(args);
  const params = new URLSearchParams();
  params.set("database-id", String(queryParams["database-id"]));
  if (queryParams.schema != null) {
    params.set("schema", queryParams.schema);
  }
  if (queryParams["table-ids"] != null) {
    for (const id of queryParams["table-ids"]) {
      params.append("table-ids", String(id));
    }
  }
  return `${SCHEMA_VIEWER_BASE_URL}?${params.toString()}`;
}

export function dataStudioGlossary() {
  return `${dataStudio()}/glossary`;
}

export function dataStudioGitSync() {
  return `${dataStudio()}/git-sync`;
}

export function dataStudioSnippet(snippetId: NativeQuerySnippetId) {
  return `${dataStudioSnippets()}/${snippetId}`;
}

export function dataStudioSnippetDependencies(snippetId: NativeQuerySnippetId) {
  return `${dataStudioSnippet(snippetId)}/dependencies`;
}

export function newDataStudioSnippet() {
  return `${dataStudioSnippets()}/new`;
}

export function dataStudioArchivedSnippets() {
  return `${dataStudioSnippets()}/archived`;
}

export function dataStudioAction(actionId: WritebackActionId) {
  return `${dataStudioActions()}/${actionId}`;
}

export function dataStudioActionEdit(actionId: WritebackActionId) {
  return `${dataStudioAction(actionId)}/edit`;
}

export function dataStudioActionFields(
  actionId: WritebackActionId,
  fieldId?: string,
) {
  const fieldsUrl = `${dataStudioAction(actionId)}/fields`;
  return fieldId != null
    ? `${fieldsUrl}/${encodeURIComponent(fieldId)}`
    : fieldsUrl;
}

export function dataStudioActionRun(actionId: WritebackActionId) {
  return `${dataStudioAction(actionId)}/run`;
}

export function dataStudioActionSettings(actionId: WritebackActionId) {
  return `${dataStudioAction(actionId)}/settings`;
}

export function newDataStudioAction() {
  return `${dataStudioActions()}/new`;
}

export function dataStudioArchivedActions() {
  return `${dataStudioActions()}/archived`;
}

export function dataStudioSettings() {
  return `${dataStudio()}/settings`;
}
